import { useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { apiVisitEngine } from '@/shared/api/client'
import { queryKeys } from '@/shared/api/query-keys'
import type { AxiosRequestConfig } from 'axios'
import type { IFileBlob, IFileResponse } from '../model/file.type'

/** visit-engine's base URL already ends in `/api`, so the documented `/api/v1/files` is just `/v1/files` here. */
const FILES_PATH = '/v1/files'

/** Pulls the server-suggested name out of `Content-Disposition`, falling back to the id. */
function parseFilename(disposition: unknown, fallback: string): string {
  if (typeof disposition !== 'string') return fallback

  const utf8 = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (utf8) return decodeURIComponent(utf8[1])

  const plain = /filename="?([^";]+)"?/i.exec(disposition)
  return plain ? plain[1] : fallback
}

// ── Raw API ───────────────────────────────────────────────────────────────────

export const fileApi = {
  uploadFile: async (
    file: File,
    options?: AxiosRequestConfig,
  ): Promise<IFileResponse> => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await apiVisitEngine.post<IFileResponse>(
      FILES_PATH,
      formData,
      {
        ...options,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...options?.headers,
        },
      },
    )
    return response.data
  },

  /** Inline stream — images only; anything else answers with an error. */
  getPreview: async (id: string): Promise<Blob> => {
    const response = await apiVisitEngine.get<Blob>(
      `${FILES_PATH}/${id}/preview`,
      { responseType: 'blob' },
    )
    return response.data
  },

  downloadFile: async (id: string): Promise<IFileBlob> => {
    const response = await apiVisitEngine.get<Blob>(
      `${FILES_PATH}/${id}/download`,
      { responseType: 'blob' },
    )
    return {
      blob: response.data,
      filename: parseFilename(response.headers['content-disposition'], id),
    }
  },
}

// ── Hooks ─────────────────────────────────────────────────────────────────────

export const useUploadFileMutation = (options?: AxiosRequestConfig) =>
  useMutation({
    mutationFn: (file: File) => fileApi.uploadFile(file, options),
  })

/**
 * Fetches the preview bytes. The endpoints sit behind bearer auth, so a plain
 * `<img src={url}>` can't reach them — components render the object URL from
 * {@link useFilePreviewUrl} instead.
 */
export const useGetFilePreviewQuery = (id: string | null | undefined) =>
  useQuery({
    queryKey: queryKeys.files.preview(id ?? ''),
    queryFn: () => fileApi.getPreview(id as string),
    enabled: Boolean(id),
    staleTime: Infinity,
  })

/** Wraps the preview blob in an object URL and revokes it when it goes away. */
export const useFilePreviewUrl = (id: string | null | undefined) => {
  const query = useGetFilePreviewQuery(id)
  const [url, setUrl] = useState<string | null>(null)

  useEffect(() => {
    if (!query.data) {
      setUrl(null)
      return
    }

    const objectUrl = URL.createObjectURL(query.data)
    setUrl(objectUrl)

    return () => URL.revokeObjectURL(objectUrl)
  }, [query.data])

  return { ...query, url }
}

/** Downloads the file and hands it to the browser as a save prompt. */
export const useDownloadFileMutation = () =>
  useMutation({
    mutationFn: (id: string) => fileApi.downloadFile(id),
    onSuccess: ({ blob, filename }) => {
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      URL.revokeObjectURL(objectUrl)
    },
  })
